import React, { useMemo, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, UserPlus, Search, ShieldAlert } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../ui/alert-dialog";
import { StatusBadge } from "../StatusBadge";

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
}

/** Mobile Schedule Builder - deliberately a DIFFERENT workflow from the desktop 3-pane, not a
 * shrunk version of it. One day at a time, a full-screen employee drawer for assignment, and a
 * sticky bottom action bar. Publish requires an explicit confirmation dialog so it can never be
 * an accidental tap. */
export const MobileScheduleBuilder = ({ schedule, assignments, board, coverage, employees, templates, permissions, actions, onAssignEmployeeDate, onShiftClick }) => {
  const [selectedDate, setSelectedDate] = useState(schedule.period_start);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFilter, setDrawerFilter] = useState("ALL");
  const [drawerSearch, setDrawerSearch] = useState("");
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);

  const dayAssignments = useMemo(
    () => (assignments || []).filter((a) => a.date === selectedDate).sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [assignments, selectedDate],
  );
  const dayWarnings = (schedule.validation_warnings || []).filter((w) => w.date === selectedDate);
  const dayCoverage = (coverage?.days || []).find((d) => d.date === selectedDate);
  const isDraftEditable = ["DRAFT", "VALIDATION_REQUIRED", "BLOCKED", "READY_FOR_REVIEW"].includes(schedule.status);
  const blockingCount = (schedule.validation_warnings || []).filter((w) => w.severity === "BLOCKING").length;

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;
  const templateName = (id) => templates.find((t) => t.id === id)?.code;

  const drawerEmployees = useMemo(() => {
    return (board?.employees || []).filter((e) => {
      if (drawerFilter !== "ALL" && e.eligibility_status !== drawerFilter) return false;
      if (drawerSearch && !e.legal_name.toLowerCase().includes(drawerSearch.toLowerCase())) return false;
      return true;
    });
  }, [board, drawerFilter, drawerSearch]);

  return (
    <div className="flex flex-col h-full" data-testid="mobile-schedule-builder">
      <div className="p-3 border-b border-gray-100 space-y-2 shrink-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-gray-800 truncate">{schedule.store_location}</p>
          <StatusBadge status={schedule.status} />
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="mobile-prev-day" disabled={selectedDate <= schedule.period_start} onClick={() => setSelectedDate(addDays(selectedDate, -1))} className="p-2 rounded-lg border border-gray-200 disabled:opacity-30"><ChevronLeft size={14} /></button>
          <input
            data-testid="mobile-date-input"
            type="date"
            value={selectedDate}
            min={schedule.period_start}
            max={schedule.period_end}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-center"
          />
          <button data-testid="mobile-next-day" disabled={selectedDate >= schedule.period_end} onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="p-2 rounded-lg border border-gray-200 disabled:opacity-30"><ChevronRight size={14} /></button>
        </div>
        {blockingCount > 0 && (
          <div className="flex items-center gap-1.5 text-xs font-semibold text-red-600" data-testid="mobile-blocking-indicator">
            <ShieldAlert size={13} /> {blockingCount} blocking issue{blockingCount > 1 ? "s" : ""} in this schedule
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {dayCoverage?.configured && dayCoverage.blocks.map((b, i) => (
          <div key={i} className={`rounded-lg border p-2.5 text-xs ${b.status === "UNDERSTAFFED" ? "bg-red-50 border-red-200 text-red-700" : "bg-gray-50 border-gray-200 text-gray-600"}`} data-testid={`mobile-coverage-${i}`}>
            <span className="font-semibold">{b.start}-{b.end}{b.required_role ? ` (${b.required_role})` : ""}:</span> required {b.required}, assigned {b.assigned}
          </div>
        ))}

        {dayWarnings.map((w, i) => (
          <div key={i} className={`flex items-start gap-1.5 text-xs rounded-lg border p-2.5 ${w.severity === "BLOCKING" ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid={`mobile-warning-${i}`}>
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{w.employee_id && `${employeeName(w.employee_id)} - `}{w.message}</span>
          </div>
        ))}

        {dayAssignments.length === 0 && <p className="text-sm text-gray-500 text-center py-6" data-testid="mobile-no-shifts">No shifts assigned for this date yet.</p>}
        {dayAssignments.map((a) => (
          <button key={a.id} onClick={() => onShiftClick(a)} data-testid={`mobile-shift-${a.id}`} className="w-full text-left bg-white rounded-xl border border-gray-200 p-3 active:scale-[0.99] transition-transform">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-gray-800">{templateName(a.shift_template_id) || "Shift"}</p>
              <span className="text-xs text-gray-500">{a.start_time}-{a.end_time}{a.crosses_midnight && " (+1d)"}</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{employeeName(a.employee_id)}{a.role ? ` - ${a.role}` : ""}</p>
          </button>
        ))}

        {isDraftEditable && permissions.canEditSchedule && (
          <button data-testid="mobile-add-employee-button" onClick={() => setDrawerOpen(true)} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-blue-300 text-blue-600 font-semibold text-sm">
            <UserPlus size={15} /> Add Employee
          </button>
        )}
      </div>

      <div className="shrink-0 border-t border-gray-100 p-3 flex items-center gap-2 bg-white" data-testid="mobile-action-bar">
        {isDraftEditable && permissions.canEditSchedule && (
          <button data-testid="mobile-validate-button" onClick={actions.validate} className="flex-1 py-2.5 rounded-full bg-gray-800 text-white text-sm font-semibold">Validate</button>
        )}
        {permissions.canEditSchedule && schedule.status === "READY_FOR_REVIEW" && (
          <button data-testid="mobile-submit-review-button" onClick={actions.submitForReview} className="flex-1 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold">Submit</button>
        )}
        {permissions.canApproveSchedule && schedule.status === "UNDER_REVIEW" && (
          <button data-testid="mobile-approve-button" onClick={actions.approve} className="flex-1 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold">Approve</button>
        )}
        {permissions.canApproveSchedule && schedule.status === "APPROVED" && (
          <button data-testid="mobile-publish-button" onClick={() => setPublishConfirmOpen(true)} disabled={blockingCount > 0} className="flex-1 py-2.5 rounded-full bg-purple-600 text-white text-sm font-semibold disabled:opacity-40">Publish</button>
        )}
        {blockingCount > 0 && (
          <button
            data-testid="mobile-review-issues-button"
            onClick={() => { const first = (schedule.validation_warnings || []).find((w) => w.severity === "BLOCKING" && w.date); if (first) setSelectedDate(first.date); }}
            className="flex-1 py-2.5 rounded-full border border-red-300 text-red-600 text-sm font-semibold"
          >
            Review Issues
          </button>
        )}
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="bottom" className="h-[85vh] flex flex-col p-0" data-testid="mobile-employee-drawer">
          <SheetHeader className="p-4 border-b border-gray-100">
            <SheetTitle>Add Employee - {selectedDate}</SheetTitle>
          </SheetHeader>
          <div className="p-3 space-y-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2.5 text-gray-500" />
              <input data-testid="mobile-drawer-search" value={drawerSearch} onChange={(e) => setDrawerSearch(e.target.value)} placeholder="Search employees" className="w-full pl-8 pr-2 py-1.5 rounded-lg border border-gray-300 text-sm" />
            </div>
            <div className="flex gap-1.5">
              {["ALL", "ELIGIBLE", "WARNING", "BLOCKED"].map((f) => (
                <button key={f} data-testid={`mobile-drawer-filter-${f}`} onClick={() => setDrawerFilter(f)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${drawerFilter === f ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-500 border-gray-200"}`}>
                  {f === "ELIGIBLE" ? "Available" : f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {drawerEmployees.map((e) => (
              <button
                key={e.employee_id}
                data-testid={`mobile-drawer-employee-${e.employee_id}`}
                onClick={() => { setDrawerOpen(false); onAssignEmployeeDate(e.employee_id, selectedDate); }}
                className="w-full text-left p-3 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-800">{e.legal_name}</p>
                  <p className="text-xs text-gray-500">{e.position || "-"}</p>
                  {e.reasons?.[0] && <p className="text-[11px] text-gray-500 mt-0.5">{e.reasons[0].message}</p>}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${e.eligibility_status === "BLOCKED" ? "bg-red-50 border-red-200 text-red-700" : e.eligibility_status === "WARNING" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                  {e.eligibility_status}
                </span>
              </button>
            ))}
            {drawerEmployees.length === 0 && <p className="text-sm text-gray-500 text-center py-8">No employees match this filter.</p>}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <AlertDialogContent data-testid="mobile-publish-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish this schedule?</AlertDialogTitle>
            <AlertDialogDescription>Publishing makes this schedule immutable and visible to every assigned employee. Further changes require creating a new version.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mobile-publish-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="mobile-publish-confirm-action" onClick={() => { setPublishConfirmOpen(false); actions.publish(); }}>Publish</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
